import { inject, Injectable } from '@angular/core';
import { OnlineQuoteToolStore } from '../store';
import { PersistentStoreService } from './persistent-store.service';
import { CountryCurrencyInitializerService } from './country-currency-initializer.service';
import { OnlineQuoteToolModeService } from './modes';
import { Configuration } from '@lx/shared/types';
import { firstValueFrom } from 'rxjs';
import { LoggerService } from '@lx/shared/logger';

/**
 * Service responsible for initializing the Online Quote Tool application.
 *
 * This service is invoked via APP_INITIALIZER to ensure that all critical
 * application state is ready before the first component renders.
 *
 * Initialization Flow:
 * 1. Load mode-specific data (default or deep link)
 * 2. Initialize store with loaded data
 * 3. Detect country and fetch currency
 * 4. Setup persistent state synchronization
 *
 * @see getAppInitializers in application-initializers-providers.functions.ts
 */
@Injectable({
  providedIn: 'root',
})
export class AppInitializerService {
  private readonly store = inject(OnlineQuoteToolStore);
  private readonly persistentStoreService = inject(PersistentStoreService);
  private readonly countryCurrencyInitializerService = inject(CountryCurrencyInitializerService);
  private readonly modeService = inject(OnlineQuoteToolModeService);
  private readonly logger = inject(LoggerService);

  /**
   * Initialize the Online Quote Tool application.
   *
   * This method:
   * 1. Loads data based on mode (default or deep link via query parameters)
   * 2. Initializes the store with default, persisted, or deep link state
   * 3. Detects user's country and fetches corresponding currency
   * 4. Sets up persistent state synchronization with sessionStorage
   *
   * This is executed via APP_INITIALIZER, guaranteeing that initialization
   * completes before Angular renders any components.
   *
   * @param configuration - Application configuration from environment
   * @returns Promise that resolves to true when initialization is complete
   * @throws Error if initialization fails, preventing app bootstrap
   */
  async initialize(configuration: Configuration): Promise<boolean> {
    try {
      // Load mode-specific data (handles deep links or default state)
      await firstValueFrom(this.modeService.loadData$());

      // Initialize the store with loaded state
      // If deep link data was loaded, it's already in session storage
      // If default mode, it loads from existing session storage or defaults
      this.store.initializeStore();

      // Detect country and fetch currency
      await firstValueFrom(this.countryCurrencyInitializerService.initializeCountryAndCurrency(configuration));

      // Setup persistent state synchronization
      // This ensures state changes are persisted to sessionStorage
      this.persistentStoreService.initializePersistingState();
      return true;
    } catch (error) {
      const errorMessage = 'Failed to initialize Online Quote Tool';
      this.logger.error(errorMessage, error);
      throw new Error(errorMessage);
    }
  }
}
